/* Small building blocks shared by the Admin Dashboard tabs (rmadd- prefix). */
import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faTriangleExclamation, faChartColumn } from '@fortawesome/free-solid-svg-icons';

/** Card with a title row and optional controls on the right. */
export const Panel = ({ title, sub, actions, children, className = '' }) => (
  <section className={`rmadd-panel ${className}`}>
    {(title || actions) && (
      <header className="rmadd-panel-head">
        <div>
          {title && <h2 className="rmadd-panel-title">{title}</h2>}
          {sub && <p className="rmadd-panel-sub">{sub}</p>}
        </div>
        {actions && <div className="rmadd-panel-actions">{actions}</div>}
      </header>
    )}
    {children}
  </section>
);

/** Pill buttons: [[value, label], ...] */
export const Segmented = ({ options, value, onChange, label }) => (
  <div className="rmadd-seg" role="radiogroup" aria-label={label}>
    {options.map(([v, l]) => (
      <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'is-on' : ''} onClick={() => onChange(v)}>
        {l}
      </button>
    ))}
  </div>
);

export const ChartBox = ({ loading, error, onRetry, empty, emptyText, tall, children }) => {
  if (loading) return <div className={`rmadd-chart rmadd-skel ${tall ? 'is-tall' : ''}`} aria-busy="true" />;
  if (error)
    return (
      <div className="rmadd-state">
        <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
        <p>{error}</p>
        {onRetry && (
          <button type="button" className="rmadd-btn rmadd-btn--ghost" onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    );
  if (empty)
    return (
      <div className="rmadd-state">
        <FontAwesomeIcon icon={faChartColumn} />
        <p>{emptyText || 'No data yet.'}</p>
      </div>
    );
  return <div className={`rmadd-chart ${tall ? 'is-tall' : ''}`}>{children}</div>;
};

/** Modal rendered inside the admin layout, so it follows light/dark. */
export const DashModal = ({ title, kicker, onClose, busy, wide, danger, children, footer }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div className="rmadd-overlay" onClick={() => !busy && onClose()}>
      <div
        className={`rmadd-modal ${wide ? 'is-wide' : ''} ${danger ? 'is-danger' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="rmadd-modal-head">
          <div>
            {kicker && <span className="rmadd-modal-kicker">{kicker}</span>}
            <h2>{title}</h2>
          </div>
          <button type="button" className="rmadd-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </header>
        <div className="rmadd-modal-body">{children}</div>
        {footer && <footer className="rmadd-modal-foot">{footer}</footer>}
      </div>
    </div>
  );
};

export const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

/** Local YYYY-MM-DD (toISOString() is UTC and can be a day off). */
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** 2023 → this year (was a hard-coded list that stopped at 2026). */
export const YEARS = (() => {
  const out = [];
  for (let y = new Date().getFullYear(); y >= 2023; y -= 1) out.push(y);
  return out;
})();

export const MONTHS = Array.from({ length: 12 }, (_, i) => [
  i + 1,
  new Date(2024, i).toLocaleString('en-US', { month: 'long' }),
]);